import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { CompoundHistory, formatNumber } from '../CompoundPage.hooks';

interface CompoundPieChartProps {
  lastHistory: CompoundHistory;
  className?: string;
}

const COLORS = {
  principal: 'rgb(255, 99, 132)',
  interest: 'rgb(53, 162, 235)',
};

function CompoundPieChart({ lastHistory, className }: CompoundPieChartProps) {
  const data = useMemo(
    () => [
      { name: '원금', value: lastHistory.principalAmount },
      { name: '이자 수익', value: lastHistory.totalInterest },
    ],
    [lastHistory]
  );

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    percent: number;
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="0.875rem"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  return (
    <ResponsiveContainer className={className} width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          dataKey="value"
          labelLine={false}
          label={renderLabel}
        >
          <Cell fill={COLORS.principal} />
          <Cell fill={COLORS.interest} />
        </Pie>
        <Tooltip
          formatter={(value) => formatNumber(value as number)}
          contentStyle={{
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
          }}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          formatter={(value: string) => (
            <span style={{ color: '#374151', fontSize: '0.875rem' }}>{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default CompoundPieChart;
